// src/pages/HomePage.js
import React, { useEffect, useRef, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { collection, doc, documentId, getDoc, getDocs, query, where } from 'firebase/firestore';
import BookList from '../components/BookList';
import RecentBooks from '../components/RecentBooks';
import SiteFooter from '../components/SiteFooter';
import { getFavoriteCafeIds } from '../utils/favoriteCafes';

const DISTRICTS = [
  { slug: 'holosiivskyi',   label: 'Голосіївський' },
  { slug: 'darniytskyi',    label: 'Дарницький' },
  { slug: 'desnianskyi',    label: 'Деснянський' },
  { slug: 'dniprovskyi',    label: 'Дніпровський' },
  { slug: 'obolonskyi',     label: 'Оболонський' },
  { slug: 'pecherskyi',     label: 'Печерський' },
  { slug: 'podilskyi',      label: 'Подільський' },
  { slug: 'svjatoshynskyi', label: 'Святошинський' },
  { slug: 'solomianskyi',   label: 'Солом’янський' },
  { slug: 'shevchenkivskyi',label: 'Шевченківський' },
];

const CATEGORY_LABELS = {
  fiction: 'Історії, що захоплюють',
  nonfiction: 'Дізнатись нове',
  // kids можна повернути пізніше
};

const chunk = (arr, size = 10) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

function IntroBanner({ userId, canClose }) {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [isClosing, setIsClosing] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isFirstFlipped, setIsFirstFlipped] = useState(false);
  const [isSecondFlipped, setIsSecondFlipped] = useState(false);
  const [isThirdFlipped, setIsThirdFlipped] = useState(false);
  const [isFourthFlipped, setIsFourthFlipped] = useState(false);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isMobile = w <= 540;
  const isTablet = w > 540 && w <= 900;
  const grid = isMobile ? 'repeat(2, minmax(0, 1fr))' : (isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))');
  const CLOSE_ANIM_MS = 900;
  const bannerStorageKey = userId ? `introBannerClosed:${userId}` : '';

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!bannerStorageKey) {
      setIsHidden(false);
      setIsClosing(false);
      return;
    }

    const closed = localStorage.getItem(bannerStorageKey) === '1';
    setIsHidden(closed);
    setIsClosing(false);
  }, [bannerStorageKey]);

  const handleClose = () => {
    if (!canClose) return;
    if (isClosing) return;
    setIsClosing(true);
    if (bannerStorageKey) {
      localStorage.setItem(bannerStorageKey, '1');
    }
    closeTimerRef.current = setTimeout(() => {
      setIsHidden(true);
    }, CLOSE_ANIM_MS);
  };

  const steps = [
    { step: '1', image: '/images/intro/step-1.png', fallbackImage: '/images/intro/step-1.svg', title: 'Принеси книгу', text: 'в кавʼярню' },
    { step: '2', image: '/images/intro/step-2.png', fallbackImage: '/images/intro/step-2.svg', title: 'Отримай монети', text: 'на баланс' },
    { step: '3', image: '/images/intro/step-3.png', fallbackImage: '/images/intro/step-3.svg', title: 'Витрачай їх, щоб', text: 'взяти нову книгу' },
    { step: '4', image: '/images/intro/step-4.png', fallbackImage: '/images/intro/step-4.svg', title: 'Приходь знову', text: 'і повторюй цикл' },
  ];
  const step5 = { step: '5', image: '/images/intro/step-5.png', fallbackImage: '/images/intro/step-5.svg', title: 'ÐšÑ€Ð¾Ðº 5', text: '' };
  const step6 = { step: '6', image: '/images/intro/step-6.png', fallbackImage: '/images/intro/step-6.svg', title: 'Step 6', text: '' };
  const step7 = { step: '7', image: '/images/intro/step-7.png', fallbackImage: '/images/intro/step-7.svg', title: 'Step 7', text: '' };
  const step8 = { step: '8', image: '/images/intro/step-8.png', fallbackImage: '/images/intro/step-8.svg', title: 'Step 8', text: '' };

  if (isHidden) return null;

  return (
    <section
      className={`intro-banner ${isClosing ? 'closing' : ''}`}
      style={{
        background:
          'radial-gradient(900px 300px at 0% 100%, #e7efe7 0%, transparent 60%), radial-gradient(900px 300px at 100% 100%, #e7efe7 0%, transparent 60%), linear-gradient(180deg, #f7faf7 0%, #eef4ee 100%)',
        border: '1px solid #dbe7dc',
        borderRadius: 20,
        padding: isMobile ? '16px 12px' : '22px 20px',
        marginBottom: 18,
        position: 'relative',
        overflow: 'hidden',
        pointerEvents: isClosing ? 'none' : 'auto',
      }}
    >
      <style>{`
        .intro-step-card.flip-card {
          perspective: 1200px;
          cursor: pointer;
        }
        .intro-step-card.flip-card .flip-inner {
          position: relative;
          width: 100%;
          height: auto;
          aspect-ratio: 1 / 1;
          transform-style: preserve-3d;
          transition: transform 620ms cubic-bezier(0.2, 0.8, 0.2, 1);
          will-change: transform;
        }
        .intro-step-card.flip-card .flip-face {
          position: absolute;
          inset: 0;
          display: grid;
          align-items: center;
          justify-items: center;
          backface-visibility: hidden;
        }
        .intro-step-card.flip-card .flip-back {
          transform: rotateY(180deg);
        }
        .intro-step-card.flip-card.is-flipped .flip-inner {
          transform: rotateY(180deg);
        }
        @media (prefers-reduced-motion: reduce) {
          .intro-step-card.flip-card .flip-inner {
            transition: none;
          }
        }
        .intro-banner.closing .intro-banner-title {
          animation: introTitleOut 220ms ease forwards;
        }
        .intro-banner.closing .intro-step-card {
          animation:
            introGather 300ms cubic-bezier(0.22, 0.78, 0.2, 1) forwards,
            introFlyOut 540ms cubic-bezier(0.16, 1, 0.3, 1) 300ms forwards;
          will-change: transform, opacity;
          backface-visibility: hidden;
          transform: translateZ(0);
        }
        .intro-banner.closing .intro-close-btn {
          animation: introCloseFade 180ms ease forwards;
        }
        @keyframes introTitleOut {
          to { opacity: 0; transform: translateY(-8px); }
        }
        @keyframes introCloseFade {
          to { opacity: 0; transform: scale(0.9); }
        }
        @keyframes introGather {
          from {
            transform: translate3d(0, 0, 0) rotate(0deg) scale(1);
            opacity: 1;
          }
          to {
            transform: translate3d(calc(82px - (var(--i) * 14px)), calc((1.5 - var(--i)) * 9px), 0)
              rotate(calc((var(--i) - 1.5) * 2deg)) scale(0.94);
            opacity: 1;
          }
        }
        @keyframes introFlyOut {
          from {
            transform: translate3d(calc(82px - (var(--i) * 14px)), calc((1.5 - var(--i)) * 9px), 0)
              rotate(calc((var(--i) - 1.5) * 2deg)) scale(0.94);
            opacity: 1;
          }
          to {
            transform: translate3d(820px, -16px, 0) rotate(10deg) scale(0.88);
            opacity: 0;
          }
        }
      `}</style>
      {canClose ? (
        <button
          type="button"
          onClick={handleClose}
          className="intro-close-btn"
          aria-label="Закрити банер"
          title="Закрити"
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 34,
            height: 34,
            borderRadius: 999,
            border: '1px solid #cfe0d0',
            background: '#ffffffd9',
            color: '#14532d',
            fontSize: 22,
            lineHeight: '30px',
            cursor: 'pointer',
            zIndex: 3,
          }}
        >
          ×
        </button>
      ) : null}
      <h2
        className="intro-banner-title"
        style={{
          margin: '0 0 14px',
          textAlign: 'center',
          color: '#14532d',
          fontSize: isMobile ? 26 : 42,
          lineHeight: 1.08,
          fontWeight: 900,
        }}
      >
        Буккросинг в кав'ярні - обмін книгами легко
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 12 }}>
        {steps.map((s, idx) => (
          idx === 0 ? (
            <div
              key={s.title}
              className={`intro-step-card flip-card ${isFirstFlipped ? 'is-flipped' : ''}`}
              onClick={() => setIsFirstFlipped((prev) => !prev)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsFirstFlipped((prev) => !prev);
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={isFirstFlipped}
              aria-label="Перевернути картку"
              style={{
                '--i': idx,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 16,
                padding: '10px',
                display: 'grid',
                justifyItems: 'center',
                textAlign: 'center',
              }}
            >
              <div className="flip-inner">
                <div className="flip-face flip-front">
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      borderRadius: 12,
                      background: '#fff',
                      border: '1px solid #e1e9e1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={s.image}
                      alt={s.title}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        const triedFallback = e.currentTarget.dataset.fallbackTried === '1';
                        if (!triedFallback && s.fallbackImage) {
                          e.currentTarget.dataset.fallbackTried = '1';
                          e.currentTarget.src = s.fallbackImage;
                          return;
                        }
                        e.currentTarget.style.display = 'none';
                        const fb = e.currentTarget.nextSibling;
                        if (fb) fb.style.display = 'flex';
                      }}
                    />
                    <div
                      aria-hidden
                      style={{
                        display: 'none',
                        width: 54,
                        height: 54,
                        borderRadius: 999,
                        background: '#ecfdf3',
                        border: '1px solid #bbf7d0',
                        color: '#166534',
                        fontWeight: 900,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {s.step}
                    </div>
                  </div>
                </div>
                <div className="flip-face flip-back">
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      borderRadius: 12,
                      background: '#fff',
                      border: '1px solid #e1e9e1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={step5.image}
                      alt={step5.title}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        const triedFallback = e.currentTarget.dataset.fallbackTried === '1';
                        if (!triedFallback && step5.fallbackImage) {
                          e.currentTarget.dataset.fallbackTried = '1';
                          e.currentTarget.src = step5.fallbackImage;
                          return;
                        }
                        e.currentTarget.style.display = 'none';
                        const fb = e.currentTarget.nextSibling;
                        if (fb) fb.style.display = 'flex';
                      }}
                    />
                    <div
                      aria-hidden
                      style={{
                        display: 'none',
                        width: 54,
                        height: 54,
                        borderRadius: 999,
                        background: '#ecfdf3',
                        border: '1px solid #bbf7d0',
                        color: '#166534',
                        fontWeight: 900,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {step5.step}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : idx === 1 ? (
            <div
              key={s.title}
              className={`intro-step-card flip-card ${isSecondFlipped ? 'is-flipped' : ''}`}
              onClick={() => setIsSecondFlipped((prev) => !prev)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsSecondFlipped((prev) => !prev);
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={isSecondFlipped}
              aria-label="Перевернути картку 2"
              style={{
                '--i': idx,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 16,
                padding: '10px',
                display: 'grid',
                justifyItems: 'center',
                textAlign: 'center',
              }}
            >
              <div className="flip-inner">
                <div className="flip-face flip-front">
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      borderRadius: 12,
                      background: '#fff',
                      border: '1px solid #e1e9e1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={s.image}
                      alt={s.title}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        const triedFallback = e.currentTarget.dataset.fallbackTried === '1';
                        if (!triedFallback && s.fallbackImage) {
                          e.currentTarget.dataset.fallbackTried = '1';
                          e.currentTarget.src = s.fallbackImage;
                          return;
                        }
                        e.currentTarget.style.display = 'none';
                        const fb = e.currentTarget.nextSibling;
                        if (fb) fb.style.display = 'flex';
                      }}
                    />
                    <div
                      aria-hidden
                      style={{
                        display: 'none',
                        width: 54,
                        height: 54,
                        borderRadius: 999,
                        background: '#ecfdf3',
                        border: '1px solid #bbf7d0',
                        color: '#166534',
                        fontWeight: 900,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {s.step}
                    </div>
                  </div>
                </div>
                <div className="flip-face flip-back">
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      borderRadius: 12,
                      background: '#fff',
                      border: '1px solid #e1e9e1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={step6.image}
                      alt={step6.title}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        const triedFallback = e.currentTarget.dataset.fallbackTried === '1';
                        if (!triedFallback && step6.fallbackImage) {
                          e.currentTarget.dataset.fallbackTried = '1';
                          e.currentTarget.src = step6.fallbackImage;
                          return;
                        }
                        e.currentTarget.style.display = 'none';
                        const fb = e.currentTarget.nextSibling;
                        if (fb) fb.style.display = 'flex';
                      }}
                    />
                    <div
                      aria-hidden
                      style={{
                        display: 'none',
                        width: 54,
                        height: 54,
                        borderRadius: 999,
                        background: '#ecfdf3',
                        border: '1px solid #bbf7d0',
                        color: '#166534',
                        fontWeight: 900,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {step6.step}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : idx === 2 ? (
            <div
              key={s.title}
              className={`intro-step-card flip-card ${isThirdFlipped ? 'is-flipped' : ''}`}
              onClick={() => setIsThirdFlipped((prev) => !prev)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsThirdFlipped((prev) => !prev);
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={isThirdFlipped}
              aria-label="Перевернути картку 3"
              style={{
                '--i': idx,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 16,
                padding: '10px',
                display: 'grid',
                justifyItems: 'center',
                textAlign: 'center',
              }}
            >
              <div className="flip-inner">
                <div className="flip-face flip-front">
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      borderRadius: 12,
                      background: '#fff',
                      border: '1px solid #e1e9e1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={s.image}
                      alt={s.title}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        const triedFallback = e.currentTarget.dataset.fallbackTried === '1';
                        if (!triedFallback && s.fallbackImage) {
                          e.currentTarget.dataset.fallbackTried = '1';
                          e.currentTarget.src = s.fallbackImage;
                          return;
                        }
                        e.currentTarget.style.display = 'none';
                        const fb = e.currentTarget.nextSibling;
                        if (fb) fb.style.display = 'flex';
                      }}
                    />
                    <div
                      aria-hidden
                      style={{
                        display: 'none',
                        width: 54,
                        height: 54,
                        borderRadius: 999,
                        background: '#ecfdf3',
                        border: '1px solid #bbf7d0',
                        color: '#166534',
                        fontWeight: 900,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {s.step}
                    </div>
                  </div>
                </div>
                <div className="flip-face flip-back">
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      borderRadius: 12,
                      background: '#fff',
                      border: '1px solid #e1e9e1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={step7.image}
                      alt={step7.title}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        const triedFallback = e.currentTarget.dataset.fallbackTried === '1';
                        if (!triedFallback && step7.fallbackImage) {
                          e.currentTarget.dataset.fallbackTried = '1';
                          e.currentTarget.src = step7.fallbackImage;
                          return;
                        }
                        e.currentTarget.style.display = 'none';
                        const fb = e.currentTarget.nextSibling;
                        if (fb) fb.style.display = 'flex';
                      }}
                    />
                    <div
                      aria-hidden
                      style={{
                        display: 'none',
                        width: 54,
                        height: 54,
                        borderRadius: 999,
                        background: '#ecfdf3',
                        border: '1px solid #bbf7d0',
                        color: '#166534',
                        fontWeight: 900,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {step7.step}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : idx === 3 ? (
            <div
              key={s.title}
              className={`intro-step-card flip-card ${isFourthFlipped ? 'is-flipped' : ''}`}
              onClick={() => setIsFourthFlipped((prev) => !prev)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsFourthFlipped((prev) => !prev);
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={isFourthFlipped}
              aria-label="Перевернути картку 4"
              style={{
                '--i': idx,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 16,
                padding: '10px',
                display: 'grid',
                justifyItems: 'center',
                textAlign: 'center',
              }}
            >
              <div className="flip-inner">
                <div className="flip-face flip-front">
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      borderRadius: 12,
                      background: '#fff',
                      border: '1px solid #e1e9e1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={s.image}
                      alt={s.title}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        const triedFallback = e.currentTarget.dataset.fallbackTried === '1';
                        if (!triedFallback && s.fallbackImage) {
                          e.currentTarget.dataset.fallbackTried = '1';
                          e.currentTarget.src = s.fallbackImage;
                          return;
                        }
                        e.currentTarget.style.display = 'none';
                        const fb = e.currentTarget.nextSibling;
                        if (fb) fb.style.display = 'flex';
                      }}
                    />
                    <div
                      aria-hidden
                      style={{
                        display: 'none',
                        width: 54,
                        height: 54,
                        borderRadius: 999,
                        background: '#ecfdf3',
                        border: '1px solid #bbf7d0',
                        color: '#166534',
                        fontWeight: 900,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {s.step}
                    </div>
                  </div>
                </div>
                <div className="flip-face flip-back">
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      borderRadius: 12,
                      background: '#fff',
                      border: '1px solid #e1e9e1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={step8.image}
                      alt={step8.title}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        const triedFallback = e.currentTarget.dataset.fallbackTried === '1';
                        if (!triedFallback && step8.fallbackImage) {
                          e.currentTarget.dataset.fallbackTried = '1';
                          e.currentTarget.src = step8.fallbackImage;
                          return;
                        }
                        e.currentTarget.style.display = 'none';
                        const fb = e.currentTarget.nextSibling;
                        if (fb) fb.style.display = 'flex';
                      }}
                    />
                    <div
                      aria-hidden
                      style={{
                        display: 'none',
                        width: 54,
                        height: 54,
                        borderRadius: 999,
                        background: '#ecfdf3',
                        border: '1px solid #bbf7d0',
                        color: '#166534',
                        fontWeight: 900,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {step8.step}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              key={s.title}
              className="intro-step-card"
              style={{
                '--i': idx,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 16,
                padding: '10px',
                display: 'grid',
                justifyItems: 'center',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '100%',
                  aspectRatio: '1 / 1',
                  borderRadius: 12,
                  background: '#fff',
                  border: '1px solid #e1e9e1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={s.image}
                  alt={s.title}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={(e) => {
                    const triedFallback = e.currentTarget.dataset.fallbackTried === '1';
                    if (!triedFallback && s.fallbackImage) {
                      e.currentTarget.dataset.fallbackTried = '1';
                      e.currentTarget.src = s.fallbackImage;
                      return;
                    }
                    e.currentTarget.style.display = 'none';
                    const fb = e.currentTarget.nextSibling;
                    if (fb) fb.style.display = 'flex';
                  }}
                />
                <div
                  aria-hidden
                  style={{
                    display: 'none',
                    width: 54,
                    height: 54,
                    borderRadius: 999,
                    background: '#ecfdf3',
                    border: '1px solid #bbf7d0',
                    color: '#166534',
                    fontWeight: 900,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {s.step}
                </div>
              </div>
            </div>
          )
        ))}
      </div>
    </section>
  );
}

function SectionHeader({ title, onMore, right }) {
  const [hover, setHover] = useState(false);
  const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const isNarrow = w <= 540;
  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr auto',alignItems:'center',marginBottom:8}}>
      <h2 style={{margin:0, color:'#2f2f2f'}}>{title}</h2>
      <div style={{display:'flex',gap:12,alignItems:'center'}}>
        {right}
        <button
          onClick={onMore}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            background:'transparent', border:'none', fontWeight:700, fontSize:16,
            cursor:'pointer', display:'inline-flex', alignItems:'center', gap:8,
            color: hover ? '#f0a500' : '#2f2f2f', transition:'color .15s ease'
          }}
        >
          {!isNarrow && 'Побачити більше'}
          <span style={{
            transform: hover ? 'translateX(3px)' : 'translateX(0)',
            transition: 'transform .15s ease'
          }}>→</span>

        </button>
      </div>
    </div>
  );
}

function CategorySection({ slug, navigate }) {
  return (
    <>
      <SectionHeader
        title={CATEGORY_LABELS[slug]}
        onMore={() => navigate(`/category?name=${slug}`)}
      />
      <BookList onlyAvailable limitCount={4} category={slug} />
    </>
  );
}

const HomePage = () => {
  const [user, loading] = useAuthState(auth);
  const role = localStorage.getItem('userRole');
  const isBarista = role === 'barista';
  const navigate = useNavigate();
  const [isPhone, setIsPhone] = useState(typeof window !== 'undefined' ? window.innerWidth <= 540 : false);

  const [district, setDistrict] = useState('svjatoshynskyi');
  const [favoriteCafeNames, setFavoriteCafeNames] = useState([]);
  const [canDismissIntroBanner, setCanDismissIntroBanner] = useState(false);

  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth <= 540);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    (async () => {
      if (!user?.uid) {
        setCanDismissIntroBanner(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (!snap.exists()) {
          setCanDismissIntroBanner(false);
          return;
        }
        const data = snap.data() || {};
        const uniqueAdded = Number(data.uniqueAdded || 0);
        const booksTaken = Number(data.booksTaken || 0);
        setCanDismissIntroBanner(uniqueAdded > 0 || booksTaken > 0);
      } catch {
        setCanDismissIntroBanner(false);
      }
    })();
  }, [user?.uid]);

  useEffect(() => {
    (async () => {
      if (!user?.uid) {
        setFavoriteCafeNames([]);
        return;
      }

      try {
        const ids = await getFavoriteCafeIds(user.uid);
        if (!ids.length) {
          setFavoriteCafeNames([]);
          return;
        }

        const names = [];
        for (const pack of chunk(ids, 10)) {
          const cafesSnap = await getDocs(
            query(collection(db, 'cafes'), where(documentId(), 'in', pack))
          );
          cafesSnap.docs.forEach((d) => {
            const name = d.data()?.cafeName;
            if (name) names.push(name);
          });
        }
        setFavoriteCafeNames(Array.from(new Set(names)));
      } catch {
        setFavoriteCafeNames([]);
      }
    })();
  }, [user?.uid]);

  useEffect(() => {
    if (!loading && isBarista) {
      navigate('/barista/panel', { replace: true });
    }
  }, [loading, isBarista, navigate]);

  if (loading || isBarista) return null;

  return (
    <div style={{ background:'#F6F4EF', minHeight:'100vh' }}>
      <main style={{ maxWidth:1100, margin:'0 auto', padding:'20px 16px' }}>
        <IntroBanner userId={user?.uid || ''} canClose={canDismissIntroBanner} />

        {/* Популярні */}
        <SectionHeader title="Популярні" onMore={() => navigate('/popular')} />
        <BookList onlyAvailable limitCount={8} orderByBorrowed />

        <SectionHeader
          title="Книги у вибраних кав'ярнях"
          onMore={() => navigate('/favorite-cafes-books')}
        />
        {favoriteCafeNames.length === 0 ? (
          <div style={{ color: '#6b7280', marginBottom: 24 }}>
            Ви ще не позначили жодну кав'ярню як вибрану. Для вибору натисніть сердечко на сторінці кав'ярні,
            переглянути список кав'ярень можете{' '}
            <button
              type="button"
              onClick={() => navigate('/cafes')}
              style={{
                background: 'none',
                border: 'none',
                color: '#14532d',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                fontWeight: 600,
              }}
            >
              тут
            </button>
            .
          </div>
        ) : (
          <BookList
            onlyAvailable
            limitCount={isPhone ? 6 : 8}
            filterByCafeNames={favoriteCafeNames}
          />
        )}

        {/* Книги в твоєму районі */}
        <SectionHeader
          title="Книги в твоєму районі"
          onMore={() => navigate(`/near?district=${encodeURIComponent(district)}`)}
          right={
            <select
              value={district}
              onChange={e => setDistrict(e.target.value)}
              style={{
                padding:'8px 10px', borderRadius:8, border:'1px solid #e5e7eb',
                background:'#fff', fontSize:14
              }}
            >
              {DISTRICTS.map(d => <option key={d.slug} value={d.slug}>{d.label}</option>)}
            </select>
          }
        />
        <BookList onlyAvailable limitCount={8} filterByDistrict={district} />

        {/* Категорії (по 4 картки + кнопка "Побачити більше" як у "Популярні") */}
        <CategorySection slug="fiction" navigate={navigate} />
        <CategorySection slug="nonfiction" navigate={navigate} />
        {/* <CategorySection slug="kids" navigate={navigate} />  // повернемо, коли знадобиться */}
        {/* Останні додані */}
        <SectionHeader
          title="Останні додані"
          onMore={() => navigate('/category?name=recent')} // тимчасово можна вести на загальний список
        />
        <RecentBooks limitCount={8} />

      </main>
      <SiteFooter />
    </div>
  );
};

export default HomePage;

